'use client';

import { useRef } from 'react';
import Link from 'next/link';
import { faDesktop } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SuggestionSection } from './suggestion-section.component';

import { Icon } from '@/components/ui/icon.component';
import { SearchCardComponent } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/search-card.component';
import {
  TelemedProcedure,
  hasRenderableTelemedSuggestions,
} from '@/app/(main)/(pages)/busca/[slug]/_services/get-telemed-procedure.ts.service';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useUserLocation } from '@/hooks/use-user-location.hook';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { SearchType } from '@/types/most-search-type';

interface TelehealthSuggestionProps {
  telehealthSuggestions: TelemedProcedure | null;
  proceduresResearched: CodesOrSlugsResponse;
}

export function TelehealthSuggestion({
  telehealthSuggestions,
  proceduresResearched,
}: TelehealthSuggestionProps) {
  const analytics = getAnalyticsPlugin();
  const { userLocation } = useUserLocation();
  const { resolveLink } = useSearch();
  const hasTrackedClickRef = useRef(false);
  const [telemedProcedure] = telehealthSuggestions?.telemedProcedures ?? [];

  if (!hasRenderableTelemedSuggestions(telehealthSuggestions) || !telemedProcedure) {
    return null;
  }

  const [featured, previewSchedule] = telehealthSuggestions.schedules;

  const trackFeaturedClick = () => {
    if (hasTrackedClickRef.current) {
      return;
    }

    hasTrackedClickRef.current = true;
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_TELEHEALTH_OPTION_CLICKED, {
      partner_id: featured.partner?.id,
      professional_id: featured.professional?.id,
      price: featured.procedures?.[0]?.price,
    });
  };

  const trackSeeAll = () => {
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_TELEHEALTH_SEE_ALL, {
      procedure_code: telemedProcedure.code,
      city: userLocation?.city,
      qtd_opcoes: telehealthSuggestions.schedules.length,
    });
  };

  const telemedSearchResult: SearchResult = {
    slug: telemedProcedure.slug,
    code: telemedProcedure.code,
    searchType: telemedProcedure.searchType as SearchType,
    displayName: telemedProcedure.name,
  };
  const href = resolveLink(telemedSearchResult);

  return (
    <SuggestionSection
      icon={<Icon icon={faDesktop} className="text-xl text-green-900" />}
      title="Teleconsulta para você se consultar de qualquer lugar!"
      featuredCard={(
        <div onClickCapture={trackFeaturedClick}>
          <SearchCardComponent
            fluid
            schedule={featured}
            searchType={telemedProcedure.searchType}
            proceduresResearched={proceduresResearched}
          />
        </div>
      )}
      previewCard={previewSchedule && (
        <SearchCardComponent
          fluid
          schedule={previewSchedule}
          searchType={telemedProcedure.searchType}
          proceduresResearched={proceduresResearched}
        />
      )}
      cta={(
        <Link
          href={href}
          onClick={trackSeeAll}
          data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_TELEHEALTH_CTA}
          className="flex w-full items-center justify-center rounded-lg bg-success-300 px-4 py-3 text-lg font-semibold text-success-800 transition-colors hover:bg-success-400 hover:text-success-900 active:bg-success-500"
        >
          Ver mais profissionais
        </Link>
      )}
    />
  );
}
